/* eslint-disable spaced-comment */
/* eslint-disable react/prop-types */
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  TreeSelect,
} from 'antd';
import Table from 'components/Table';

import { checkToken } from 'api/utils';
import React, { useEffect, useState } from 'react';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import {
  deleteEntidadMunicipalOtrosDatos,
  getEntidadesMunicipalesOtrosDatos,
  patchEntidadMunicipalOtrosDatos,
  postEntidadMunicipalOtrosDatos,
} from 'api/nominas/entidades/entidadMunicipalOtrosDatos';
import { getAreas, getContentTypesUR, getEstadosGlobales } from 'api/catalogos';
import { areaBuscar, normalizeData } from 'utils/normalizers';
import { onError } from 'utils/handlers';
import ModalDelete from 'components/ModalDelete';
import { generateEmailRules } from 'utils/rules';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO, ESTATUS_INACTIVO } from 'utils/constants';

export const tabOtrosDatosPermission = {
  model: 'entidadmunicipalotrosdatos',
};

const EntidadMunicipalOtrosDatos = ({ permission }) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteMesage, setDeleteMessage] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [areas, setAreas] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [estadosGlobales, setEstadosGlobales] = useState([]);

  const [data, setData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const _entidadesMunicipalesOtrosDatos = await getEntidadesMunicipalesOtrosDatos();
    setData(_entidadesMunicipalesOtrosDatos);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _data,
        _areas,
        _estadosGlobales,
        _contentTypes,
      ] = await Promise.all([
        getEntidadesMunicipalesOtrosDatos(),
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getEstadosGlobales(),
        getContentTypesUR(),
      ]);

      const formattedURS = normalizeData(_areas);

      if (mounted) {
        setData(_data); // Entidades Municipales Otros Datos
        setAreas(formattedURS);
        setEstadosGlobales(_estadosGlobales.filter(
          (estatus) => estatus.id === ESTATUS_ACTIVO || estatus.id === ESTATUS_INACTIVO,
        ));
        setContentTypes(_contentTypes);
      }
      setLoading(false);
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const columns = [
    {
      key: 'concepto',
      dataIndex: 'concepto',
      titleText: 'Concepto',
      width: 200,
    },
    {
      key: 'correo_electronico',
      dataIndex: 'correo_electronico',
      titleText: 'Correo Electrónico',
      width: 200,
    },
    {
      key: 'area_content_id',
      dataIndex: 'area_content_id',
      titleText: 'Área',
      width: 200,
      render: (value) => areaBuscar(value, areas)?.title,
    },
    {
      key: 'responsable',
      dataIndex: 'responsable',
      titleText: 'Responsable',
      width: 200,
    },
    {
      key: 'estados_globales',
      dataIndex: 'estados_globales',
      titleText: 'Estatus',
      width: 200,
      render: (value) => estadosGlobales.find(
        (estadoGlobal) => estadoGlobal.id === value,
      )?.descripcion,
    },
  ];

  const onCancel = () => {
    setSelectedRowKeys([]);
    setOpen(false);
    form.resetFields();
  };

  const onClickEdit = async () => {
    setLoading(true);

    const [key] = selectedRowKeys;
    const _entidadMunicipalOtroDato = data.find((modelo) => modelo?.id === key);

    //? Buscamos el area para ubicarla en el TreeSelect:
    const _area = areaBuscar(_entidadMunicipalOtroDato.area_content_id, areas);
    _entidadMunicipalOtroDato.area_content_id = _area?.value;

    if (_entidadMunicipalOtroDato) {
      form.resetFields();
      form.setFieldsValue(_entidadMunicipalOtroDato);
      setOpen(true);
    }

    setLoading(false);
  };

  const onClickAdd = () => {
    onCancel();
    setOpen(true);
  };

  const onClickDelete = () => {
    setOpenDelete(true);
  };

  const onDelete = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteEntidadMunicipalOtrosDatos(key);
        if (deleted) {
          setOpenDelete(false);
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const getURValuesToSend = () => {
    const unidad_responsable = form.getFieldValue('area_content_id');
    if (unidad_responsable) {
      const [_area, _nivel] = unidad_responsable.split('/');
      const area_content_id = contentTypes.find((ct) => ct.nivel.toString() === _nivel).id;
      return { _area, area_content_id };
    }
    return { _area: undefined, area_content_id: undefined };
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();

      const _contentValues = getURValuesToSend();

      const values = {
        ...form.getFieldsValue(),
        area: _contentValues?.area_content_id,
        area_content_id: _contentValues?._area,
      };

      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchEntidadMunicipalOtrosDatos(key, values)
        : postEntidadMunicipalOtrosDatos(values));

      if (saved) {
        await fetchData();
      }

      setLoading(false);
      setOpen(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);

    // Establecer mensaje al presionar borrar:
    setDeleteMessage(`${record?.concepto} - ${record?.responsable}`);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={700}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Datos`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col span={24}>
              <Form.Item
                name="concepto"
                label="Concepto"
                hasFeedback
                rules={rules.required}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="correo_electronico"
                label="Correo Electrónico"
                rules={generateEmailRules({ required: true })}
                hasFeedback
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="responsable"
                label="Responsable"
                hasFeedback
                rules={rules.required}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="area_content_id"
                label="Área"
                hasFeedback
                rules={rules.required}
              >
                <TreeSelect
                  showSearch
                  treeNodeFilterProp="title"
                  treeData={areas}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  keyProp="id"
                  labelProp="descripcion"
                  disabled={selectedRowKeys.length <= 0}
                  dataSource={estadosGlobales}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={onDelete}
          onCancel={() => setOpenDelete(false)}
          open={openDelete}
          content={`Otro Dato Seleccionado ${deleteMesage}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default EntidadMunicipalOtrosDatos;
